import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FileSignature, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/**
 * One-click renewal quotation dialog.
 *
 * Given a software item, lets the user pick a client, adjust qty / unit
 * price / validity date, and creates a draft quotation in one click.
 * On success, navigates to /quotations/<new_id>.
 */
export function RenewalQuotationDialog({ open, onOpenChange, item }) {
  const nav = useNavigate();
  const { data: clients = [] } = useQuery({
    queryKey: ["clients-lite"],
    queryFn: () => api.listClients(),
    enabled: open,
  });

  const defaultPrice = Number(
    item?.price_yearly || (Number(item?.price_monthly || 0) * 12) || item?.price || 0,
  );
  const defaultValidUntil = (() => {
    if (!item?.expiry_date) return "";
    try {
      const d = new Date(item.expiry_date + "T00:00:00");
      d.setDate(d.getDate() - 7);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      if (d < today) {
        const t = new Date(); t.setDate(t.getDate() + 14);
        return t.toISOString().slice(0, 10);
      }
      return d.toISOString().slice(0, 10);
    } catch { return ""; }
  })();

  const [clientId, setClientId] = useState("");
  const [qty, setQty] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [currency, setCurrency] = useState("USD");

  useEffect(() => {
    if (open && item) {
      setClientId("");
      setQty(String(item.stock_qty || 1));
      setUnitPrice(defaultPrice.toFixed(2));
      setValidUntil(defaultValidUntil);
      setCurrency("USD");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const mut = useMutation({
    mutationFn: (body) => api.createSoftwareRenewalQuotation(item.id, body),
    onSuccess: (quot) => {
      toast.success(`Renewal quote ${quot.quotation_number} created`);
      onOpenChange(false);
      nav(`/quotations/${quot.id}`);
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Failed to create renewal quotation");
    },
  });

  function submit() {
    if (!clientId) { toast.error("Please choose a client"); return; }
    mut.mutate({
      client_id: clientId,
      qty: Number(qty) || 0,
      unit_price: Number(unitPrice) || 0,
      valid_until: validUntil || undefined,
      currency,
    });
  }

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="renewal-quote-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="size-4 text-primary" />
            Create renewal quotation
          </DialogTitle>
          <DialogDescription>
            <span className="font-semibold">{item.name}</span>
            {item.vendor ? ` · ${item.vendor}` : ""}
            {item.expiry_date ? (
              <> · expires <span className="mono">{item.expiry_date}</span></>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="overline">Client *</Label>
            <Select value={clientId || undefined} onValueChange={setClientId}>
              <SelectTrigger data-testid="renewal-quote-client">
                <SelectValue placeholder="Choose a client…" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="overline">Seats</Label>
              <Input data-testid="renewal-quote-qty" type="number" min="1" step="1"
                     value={qty} onChange={(e) => setQty(e.target.value)} className="mono" />
            </div>
            <div>
              <Label className="overline">Unit price</Label>
              <Input data-testid="renewal-quote-price" type="number" min="0" step="0.01"
                     value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="mono" />
            </div>
            <div>
              <Label className="overline">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="renewal-quote-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="AUD">AUD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="overline">Valid until</Label>
            <Input data-testid="renewal-quote-valid" type="date"
                   value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            <p className="text-[10px] text-muted-foreground mt-1">
              Defaults to 7 days before the current expiry so the client has time to renew.
            </p>
          </div>

          <div className="rounded-md bg-emerald-50 ring-1 ring-emerald-200 p-2.5 text-xs">
            <span className="font-semibold text-emerald-800">Total: </span>
            <span className="mono text-emerald-900 font-bold">
              {(Number(qty || 0) * Number(unitPrice || 0)).toFixed(2)} {currency}
            </span>
            {" "}
            <span className="text-emerald-700 text-[11px]">(before tax)</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mut.isPending}
                  data-testid="renewal-quote-cancel">
            <X className="size-4 mr-1.5" /> Cancel
          </Button>
          <Button onClick={submit} disabled={mut.isPending || !clientId || Number(qty || 0) <= 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="renewal-quote-submit">
            <Send className="size-4 mr-1.5" />
            {mut.isPending ? "Creating…" : "Create quotation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
